import React, {useState, useEffect, useRef, useContext} from 'react';
import {Button, Tag, InlineLoading} from '@carbon/react';
import {Checkmark, Close} from '@carbon/icons-react';
import {observer} from 'mobx-react';
import {ProcessingFile} from '../../../lib/types';
import {NetworkContext} from '../../../lib/context';

interface FileHeaderProps {
    file: ProcessingFile;
    startRenaming?: boolean;
    onRenamingStarted?: () => void;
}

const splitFileName = (name: string): {base: string; ext: string} => {
    const dotIndex = name.lastIndexOf('.');
    if (dotIndex <= 0) return {base: name, ext: ''};
    return {base: name.slice(0, dotIndex), ext: name.slice(dotIndex)};
};

const FileHeader: React.FC<FileHeaderProps> = observer(({file, startRenaming, onRenamingStarted}) => {
    const store = useContext(NetworkContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const renameControllerRef = useRef<AbortController | null>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameBaseName, setRenameBaseName] = useState('');
    const [renameExtension, setRenameExtension] = useState('');
    const [renameError, setRenameError] = useState('');
    const [renameLoading, setRenameLoading] = useState(false);

    const renameValue = renameBaseName + renameExtension;

    // Sync with an in-progress rename that survived a panel close/reopen
    useEffect(() => {
        if (store.renamingFileId === file.id && !isRenaming) {
            const {base, ext} = splitFileName(file.fileName);
            setRenameBaseName(base);
            setRenameExtension(ext);
            setRenameError('');
            setRenameLoading(true);
            setIsRenaming(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (startRenaming && !isRenaming) {
            beginRename();
            onRenamingStarted?.();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startRenaming]);

    useEffect(() => {
        if (isRenaming && !renameLoading) inputRef.current?.focus();
    }, [isRenaming, renameLoading]);

    // When the store finishes a rename that was started before panel close, exit rename mode
    useEffect(() => {
        if (renameLoading && store.renamingFileId !== file.id) {
            setRenameLoading(false);
            setIsRenaming(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.renamingFileId]);

    const getStatusTag = (status: string) => {
        const statusConfig = {
            'needs-review': {type: 'red' as const, label: 'Needs Review'},
            'processing': {type: 'blue' as const, label: 'Processing'},
            'queued': {type: 'gray' as const, label: 'Queued'},
            'completed': {type: 'green' as const, label: 'Completed'}
        };
        const config = statusConfig[status as keyof typeof statusConfig] || {type: 'gray' as const, label: status};
        return <Tag type={config.type} size="sm" className="file-status-tag">{config.label}</Tag>;
    };

    const beginRename = () => {
        const {base, ext} = splitFileName(file.fileName);
        setRenameBaseName(base);
        setRenameExtension(ext);
        setRenameError('');
        setIsRenaming(true);
    };

    const cancelRename = () => {
        setIsRenaming(false);
        setRenameError('');
    };

    const confirmRename = async () => {
        const trimmed = renameValue.trim();
        if (!trimmed || trimmed === file.fileName) {
            cancelRename();
            return;
        }
        setRenameLoading(true);
        setRenameError('');
        renameControllerRef.current = new AbortController();
        try {
            await store.renameFile(file.id, trimmed, renameControllerRef.current);
            setIsRenaming(false);
        } catch (err: any) {
            const message = err?.apiError?.error?.message || err?.message || 'Failed to rename file. Please try again.';
            setRenameError(message);
        } finally {
            setRenameLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') confirmRename();
        if (e.key === 'Escape') cancelRename();
    };

    return (
        <div className="file-info-section">
            <div className="file-name-row">
                {isRenaming ? (
                    <div className="file-name-edit">
                        <div className="file-name-input-wrapper">
                            <input
                                ref={inputRef}
                                className={`file-name-input${renameError ? ' file-name-input--error' : ''}`}
                                value={renameBaseName}
                                onChange={e => { setRenameBaseName(e.target.value); setRenameError(''); }}
                                onKeyDown={handleKeyDown}
                                disabled={renameLoading}
                                aria-label="File name (without extension)"
                            />
                            {renameExtension && (
                                <span className="file-name-extension">{renameExtension}</span>
                            )}
                        </div>
                        {renameLoading ? (
                            <InlineLoading description="Renaming..." status="active" />
                        ) : (
                            <>
                                <Button
                                    kind="ghost"
                                    size="sm"
                                    renderIcon={Checkmark}
                                    iconDescription="Confirm rename"
                                    hasIconOnly
                                    tooltipPosition="bottom"
                                    onClick={confirmRename}
                                    disabled={!renameBaseName.trim() || renameValue === file.fileName}
                                />
                                <Button
                                    kind="ghost"
                                    size="sm"
                                    renderIcon={Close}
                                    iconDescription="Cancel rename"
                                    hasIconOnly
                                    tooltipPosition="bottom"
                                    onClick={cancelRename}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <h2 className="file-name">{file.fileName}</h2>
                )}
                {!isRenaming && getStatusTag(file.status)}
            </div>
            {renameError && (
                <p className="file-name-error">{renameError}</p>
            )}
            <p className="upload-meta">{file.uploadDetails.uploadedAt}</p>
            <p className="upload-meta">Uploaded by {file.uploadDetails.uploadedBy}</p>
        </div>
    );
});

export default FileHeader;
